// Earth radius in metres (WGS84 mean sphere used by Web Mercator).
const EARTH_RADIUS_M = 6378137;
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

// Great-circle distance in metres between two [lng, lat] points.
export function haversineMeters(
  a: [number, number],
  b: [number, number]
): number {
  const φ1 = a[1] * DEG2RAD;
  const φ2 = b[1] * DEG2RAD;
  const dφ = (b[1] - a[1]) * DEG2RAD;
  const dλ = (b[0] - a[0]) * DEG2RAD;
  const s =
    Math.sin(dφ / 2) ** 2 +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(dλ / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(s));
}

// Geodesic destination from `start` given `bearing` (radians, clockwise from
// north) and `distance` in metres. Returns [lng, lat].
function destinationPoint(
  start: [number, number],
  bearingRad: number,
  distanceM: number
): [number, number] {
  const δ = distanceM / EARTH_RADIUS_M;
  const φ1 = start[1] * DEG2RAD;
  const λ1 = start[0] * DEG2RAD;
  const sinφ2 =
    Math.sin(φ1) * Math.cos(δ) +
    Math.cos(φ1) * Math.sin(δ) * Math.cos(bearingRad);
  const φ2 = Math.asin(sinφ2);
  const λ2 =
    λ1 +
    Math.atan2(
      Math.sin(bearingRad) * Math.sin(δ) * Math.cos(φ1),
      Math.cos(δ) - Math.sin(φ1) * sinφ2
    );
  return [λ2 * RAD2DEG, φ2 * RAD2DEG];
}

// Geodesic circle ring around `center` passing through `edge`. Ring is closed
// (first == last).
export function buildCircleRing(
  center: [number, number],
  edge: [number, number],
  steps = 64
): [number, number][] {
  const radiusM = haversineMeters(center, edge);
  const ring: [number, number][] = [];
  for (let i = 0; i < steps; i++) {
    const bearing = (i / steps) * Math.PI * 2;
    ring.push(destinationPoint(center, bearing, radiusM));
  }
  ring.push(ring[0]);
  return ring;
}

export function centroidCoords(coords: [number, number][]): [number, number] {
  if (!coords.length) return [0, 0];
  const lng = coords.reduce((s, c) => s + c[0], 0) / coords.length;
  const lat = coords.reduce((s, c) => s + c[1], 0) / coords.length;
  return [lng, lat];
}

export function openPolygonRing(
  ring: [number, number][]
): [number, number][] {
  if (ring.length < 2) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] === last[0] && first[1] === last[1]) {
    return ring.slice(0, -1);
  }
  return ring;
}

// Lightweight circle test: centroid + 4 evenly spaced rim samples with
// similar geodesic distance (works for our ~64-vertex circles and simpler
// approximations).
export function isCircleLikePolygon(openRing: [number, number][]): boolean {
  const n = openRing.length;
  if (n < 8) return false;

  const center = centroidCoords(openRing);
  const sampleIdx = [
    0,
    Math.floor(n / 4),
    Math.floor(n / 2),
    Math.floor((3 * n) / 4),
  ];
  const dists = sampleIdx.map((i) => haversineMeters(center, openRing[i]));
  const avg = dists.reduce((a, b) => a + b, 0) / dists.length;
  if (avg < 0.5) return false;

  return dists.every((d) => Math.abs(d - avg) / avg < 0.05);
}

export function bearingRadians(
  from: [number, number],
  to: [number, number]
): number {
  const φ1 = from[1] * DEG2RAD;
  const φ2 = to[1] * DEG2RAD;
  const Δλ = (to[0] - from[0]) * DEG2RAD;
  return Math.atan2(
    Math.sin(Δλ) * Math.cos(φ2),
    Math.cos(φ1) * Math.sin(φ2) -
      Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  );
}

export function rebuildCircleAtCenter(
  newCenter: [number, number],
  oldCenter: [number, number],
  referenceRimPoint: [number, number],
  steps: number
): [number, number][] {
  const radiusM = haversineMeters(oldCenter, referenceRimPoint);
  const bearing = bearingRadians(oldCenter, referenceRimPoint);
  const edge = destinationPoint(newCenter, bearing, radiusM);
  return buildCircleRing(newCenter, edge, steps);
}

// Spherical polygon area in square metres. Uses the L'Huilier / signed
// spherical excess formula: for each consecutive pair of ring vertices the
// area of the triangle formed with the pole is accumulated. The input ring
// may be closed or open; if the last coordinate equals the first it is
// ignored. Result is always non-negative.
export function polygonAreaSquareMeters(ring: [number, number][]): number {
  const openRing = openPolygonRing(ring);
  const n = openRing.length;
  if (n < 3) return 0;

  let total = 0;
  for (let i = 0; i < n; i += 1) {
    const p1 = openRing[i];
    const p2 = openRing[(i + 1) % n];
    total +=
      (p2[0] - p1[0]) *
      DEG2RAD *
      (2 + Math.sin(p1[1] * DEG2RAD) + Math.sin(p2[1] * DEG2RAD));
  }
  return Math.abs((total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2);
}

// Longest edge of the ring in metres (over consecutive vertex pairs; the
// closing edge is included so the ring is treated as closed).
export function longestSideMeters(ring: [number, number][]): number {
  const openRing = openPolygonRing(ring);
  const n = openRing.length;
  if (n < 2) return 0;

  let longest = 0;
  for (let i = 0; i < n; i += 1) {
    const a = openRing[i];
    const b = openRing[(i + 1) % n];
    const d = haversineMeters(a, b);
    if (d > longest) longest = d;
  }
  return longest;
}

// Best-effort radius (metres) of a circle-approximating polygon: the average
// haversine distance from the centroid to every rim vertex.
export function estimateCircleRadiusMeters(
  openRing: [number, number][]
): number {
  if (openRing.length === 0) return 0;
  const center = centroidCoords(openRing);
  const sum = openRing.reduce(
    (acc, p) => acc + haversineMeters(center, p),
    0
  );
  return sum / openRing.length;
}

